import { useEffect, useState, type CSSProperties } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assetsApi, type AssetSummary } from "../api/assets";
import { structureApi } from "../api/structure";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { ConditionChip } from "../components/ConditionChip";
import { ReplacementBadge } from "../components/AssetSummaryCard";
import { formatDate, isDueDate } from "../lib/dates";
import { shortPathLabel } from "../lib/paths";
import Ic from "../components/icons.jsx";

const LIST_SIZE = 10;

/**
 * Where `/` sends a user who cannot see Početna: the first menu screen they can open (AppShell
 * order), else the profile — never an empty page.
 */
const HOME_FALLBACKS: [string, string][] = [
  ["/layout", "layouts:view"],
  ["/tipovi", "types:view"],
  ["/sabloni", "templates:view"],
  ["/lokacije", "locations:view"],
  ["/admin/korisnici", "users:view"],
  ["/admin/uloge", "roles:view"],
  ["/admin/audit", "audit:view"],
];

type Kpis = { total: number; reminderDue: number; inService: number };

/**
 * "Početna" — the "Prazne pozicije" tile (`GET /positions?mountState=EMPTY&size=1`, needs
 * `structure:view`), three KPI tiles from `GET /assets?size=1`, plus the two lists that need
 * daily attention (all asset parts need `assets:view` — never requested without it).
 */
export default function DashboardPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canViewStructure = hasPermission("structure:view");
  const canViewAssets = hasPermission("assets:view");
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [emptyPositions, setEmptyPositions] = useState<number | null>(null);
  const [due, setDue] = useState<AssetSummary[] | null>(null);
  const [service, setService] = useState<AssetSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canViewAssets) return;
    let cancelled = false;
    Promise.all([
      assetsApi.list({ size: 1 }),
      assetsApi.list({ reminderDue: true, size: LIST_SIZE, sort: "replacementReminderAt,asc" }),
      assetsApi.list({ condition: "NA_SERVISU", size: LIST_SIZE, sort: "expectedReturnAt,asc" }),
    ])
      .then(([all, dueRes, serviceRes]) => {
        if (cancelled) return;
        setKpis({ total: all.totalElements, reminderDue: dueRes.totalElements, inService: serviceRes.totalElements });
        setDue(dueRes.content);
        setService(serviceRes.content);
      })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [canViewAssets]);

  useEffect(() => {
    if (!canViewStructure) return;
    let cancelled = false;
    structureApi.list({ mountState: "EMPTY", size: 1 })
      .then((res) => { if (!cancelled) setEmptyPositions(res.totalElements); })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [canViewStructure]);

  if (!canViewStructure && !canViewAssets) {
    const target = HOME_FALLBACKS.find(([, permission]) => hasPermission(permission))?.[0] ?? "/profil";
    return <Navigate to={target} replace />;
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("dashboard.title", "Početna")}</h1>
          <div className="sub">{t("dashboard.subtitle", "Bankom CMMS")}</div>
        </div>
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <div className="kpi-grid dash-kpis">
        {canViewStructure && (
          <Link to="/struktura?mountState=EMPTY" className="kpi kpi-link">
            <div className="label"><Ic.box /> {t("dashboard.kpi.emptyPositions", "Prazne pozicije")}</div>
            <div className="val" style={emptyPositions !== null && emptyPositions > 0 ? { color: "var(--err)" } : undefined}>{emptyPositions !== null ? emptyPositions : "…"}</div>
          </Link>
        )}
        {canViewAssets && (<>
        <Link to="/asseti" className="kpi kpi-link">
          <div className="label"><Ic.tag /> {t("dashboard.kpi.assets", "Ukupno asseta")}</div>
          <div className="val">{kpis ? kpis.total : "…"}</div>
        </Link>
        <Link to="/asseti?reminderDue=true" className="kpi kpi-link">
          <div className="label"><Ic.alert /> {t("dashboard.kpi.reminderDue", "Dospeli podsetnici")}</div>
          <div className="val" style={kpis && kpis.reminderDue > 0 ? { color: "var(--err)" } : undefined}>{kpis ? kpis.reminderDue : "…"}</div>
        </Link>
        <Link to="/asseti?condition=NA_SERVISU" className="kpi kpi-link">
          <div className="label"><Ic.truck /> {t("dashboard.kpi.inService", "Na spoljnom servisu")}</div>
          <div className="val" style={kpis && kpis.inService > 0 ? { color: "var(--warn)" } : undefined}>{kpis ? kpis.inService : "…"}</div>
        </Link>
        </>)}
      </div>

      {canViewAssets && (
      <div className="dash-cols">
        <section className="card">
          <div className="card-h">
            <div>
              <h3>{t("dashboard.due.title", "Dospeli podsetnici privremenih zamena")}</h3>
              <div className="sub">{t("dashboard.due.sub", "Privremeno montirani asseti kojima je prošao datum podsetnika.")}</div>
            </div>
            <Link to="/asseti?reminderDue=true" className="btn sm" style={{ textDecoration: "none" }}>{t("dashboard.seeAll", "Svi")}</Link>
          </div>
          {due === null ? (
            <div className="empty">{t("common.loading", "Učitavanje…")}</div>
          ) : due.length === 0 ? (
            <div className="empty">{t("dashboard.due.empty", "Nema dospelih podsetnika.")}</div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl list-card">
                <thead>
                  <tr>
                    <th>{t("assets.col.tag", "Oznaka")}</th>
                    <th>{t("assets.col.position", "Logička pozicija")}</th>
                    <th>{t("assets.col.reminder", "Podsetnik")}</th>
                  </tr>
                </thead>
                <tbody>
                  {due.map((a) => {
                    const pos = a.positionId ? shortPathLabel(a.positionPath, a.positionName) : null;
                    return (
                      <tr key={a.id}>
                        <td className="mono" data-label={t("assets.col.tag", "Oznaka")}>
                          <Link to={`/asseti/${a.id}`}><b>{a.tag}</b></Link>
                          <span className="muted" style={{ display: "block", fontSize: 11.5 }}>{a.typeName}</span>
                        </td>
                        <td data-label={t("assets.col.position", "Logička pozicija")}>
                          {pos && a.positionId ? <Link to={`/struktura/${a.positionId}`} title={pos.full}>{pos.short}</Link> : "—"}
                        </td>
                        <td data-label={t("assets.col.reminder", "Podsetnik")}><ReplacementBadge asset={a} compact /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card">
          <div className="card-h">
            <div>
              <h3>{t("dashboard.service.title", "Na spoljnom servisu")}</h3>
              <div className="sub">{t("dashboard.service.sub", "Asseti u stanju „Na servisu“ sa očekivanim povratkom.")}</div>
            </div>
            <Link to="/asseti?condition=NA_SERVISU" className="btn sm" style={{ textDecoration: "none" }}>{t("dashboard.seeAll", "Svi")}</Link>
          </div>
          {service === null ? (
            <div className="empty">{t("common.loading", "Učitavanje…")}</div>
          ) : service.length === 0 ? (
            <div className="empty">{t("dashboard.service.empty", "Ništa nije na servisu.")}</div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl list-card">
                <thead>
                  <tr>
                    <th>{t("assets.col.tag", "Oznaka")}</th>
                    <th>{t("assets.col.location", "Fizička lokacija")}</th>
                    <th>{t("dashboard.service.col.return", "Očekivani povratak")}</th>
                  </tr>
                </thead>
                <tbody>
                  {service.map((a) => {
                    const late = isDueDate(a.expectedReturnAt);
                    return (
                      <tr key={a.id}>
                        <td className="mono" data-label={t("assets.col.tag", "Oznaka")}>
                          <Link to={`/asseti/${a.id}`}><b>{a.tag}</b></Link>
                          <span className="muted" style={{ display: "block", fontSize: 11.5 }}>{a.typeName}</span>
                        </td>
                        <td data-label={t("assets.col.location", "Fizička lokacija")}>
                          {a.effectiveLocation?.name ?? "—"} <ConditionChip condition={a.condition} />
                        </td>
                        <td data-label={t("dashboard.service.col.return", "Očekivani povratak")}>
                          {a.expectedReturnAt
                            ? <span className={late ? "tag err dot" : undefined}>{formatDate(a.expectedReturnAt)}</span>
                            : <span className="muted">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
